import { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'

/**
 * The shape every intermediate-node variant shares. Declared structurally so
 * one traversal serves the plain graph the editor builds and the
 * inputs-and-outputs graph generation works from.
 */
type BranchingNode = {
  node: AutomationsPresentationV1Node
  branches: BranchingNode[][]
  groupContinuations?: Record<string, BranchingNode[]>
}

/**
 * Every node reachable from a set of branches, once each. A switch's branches
 * hold its per-handle graphs, and `groupContinuations` whatever rejoins after a
 * group — both lead downstream, so both are walked.
 */
export const collectDownstreamNodes = (
  branches: BranchingNode[][]
): AutomationsPresentationV1Node[] =>
  dedupeById(branches.flatMap(collectFromBranch))

/**
 * Every node downstream of `targetId`, or `undefined` when the graph doesn't
 * reach it. The counterpart to `findUpstreamNodes`, which walks the same graph
 * in the other direction.
 */
export const findDownstreamNodes = (
  nodes: BranchingNode[],
  targetId: string
): AutomationsPresentationV1Node[] | undefined => {
  const [head, ...tail] = nodes
  if (!head) return undefined

  if (matchesNodeId(head.node.id, targetId)) {
    // Everything after the target in its own chain is downstream of it too, not
    // just what hangs off its branches.
    return dedupeById([
      ...collectDownstreamNodes(head.branches),
      ...collectFromContinuations(head),
      ...collectFromBranch(tail)
    ])
  }

  return (
    head.branches.reduce<AutomationsPresentationV1Node[] | undefined>(
      (found, branch) => found ?? findDownstreamNodes(branch, targetId),
      undefined
    ) ?? findDownstreamNodes(tail, targetId)
  )
}

const collectFromBranch = (
  branch: BranchingNode[]
): AutomationsPresentationV1Node[] =>
  branch.flatMap(entry => [
    entry.node,
    ...collectDownstreamNodes(entry.branches),
    ...collectFromContinuations(entry)
  ])

const collectFromContinuations = (
  entry: BranchingNode
): AutomationsPresentationV1Node[] =>
  Object.values(entry.groupContinuations ?? {}).flatMap(collectFromBranch)

/**
 * A branch's copy of a node carries a `__`-suffixed id, so the same node can
 * appear under two handles. Matching on the original keeps a lookup by id
 * working either way — the editor only ever stores the original.
 */
export const matchesNodeId = (
  branchNodeId: string,
  originalId: string
): boolean =>
  branchNodeId === originalId || branchNodeId.startsWith(`${originalId}__`)

/**
 * The id a branch copy was made from — what the presentation calls the node,
 * and the only id anything stored on a condition should hold.
 *
 *   review-1__0-1  ->  review-1
 */
export const originalNodeId = (branchNodeId: string): string =>
  branchNodeId.split('__')[0]

const dedupeById = (
  nodes: AutomationsPresentationV1Node[]
): AutomationsPresentationV1Node[] =>
  Array.from(new Map(nodes.map(node => [node.id, node])).values())
